// ABOUTME: Renders a custom page layout as a CSS grid with columns and widgets.
// ABOUTME: Places the columns in the three-view layout; each column is rendered by LayoutColumn.

import { Box, ThreeViewLayout } from '@ds/coolshop';
import { usePimSidebarOpen } from '@src/_new/hooks/usePimSidebarOpen';

import { CustomPageLayout } from '../types';
import { WidgetProps } from '../widgets/types';
import { LayoutColumn } from './LayoutColumn';

interface LayoutRendererProps {
    layout: CustomPageLayout;
    context: Omit<WidgetProps, 'config'>;
}
// Side panels pad their content like every PIM sidebar (PimSideBar, the offer sidebar): 24px on
// the sides, 16px at the top and bottom. Their widget cells add no padding of their own on top.
const SIDE_PANEL_PADDING_X = 3;
const SIDE_PANEL_PADDING_Y = 2;

// A CSS length usable as a side column's width: the `fr` fractions columns may carry mean
// nothing to a fixed-width panel, so they keep the default width.
const COLUMN_LENGTH_PATTERN = /^\d+(\.\d+)?(px|%|rem|em|vw)$/;

/** The side panel width a column asks for, or undefined to keep the layout's default. */
function parseColumnWidth(width?: string | null): string | undefined {
    const raw = width?.trim();
    return raw && COLUMN_LENGTH_PATTERN.test(raw) ? raw : undefined;
}

export function LayoutRenderer({ layout, context }: LayoutRendererProps) {
    const sidebarOpen = usePimSidebarOpen();
    const hasLeftColumn = layout.columns.length > 1;
    // A third column is the right side panel, as the first one of two or more is the left one.
    const rightColumn = layout.columns.length > 2 ? layout.columns[2] : undefined;
    // A side column's width, when it is a CSS length, sizes its panel.
    const leftWidth = hasLeftColumn ? parseColumnWidth(layout.columns[0].width) : undefined;
    const rightWidth = rightColumn ? parseColumnWidth(rightColumn.width) : undefined;

    return (
        <ThreeViewLayout
            overrideLeftWidth={leftWidth}
            leftOpen={sidebarOpen}
            overrideRightWidth={rightWidth}
            left={
                hasLeftColumn ? (
                    <Box
                        height="100%"
                        overflowY="auto"
                        px={SIDE_PANEL_PADDING_X}
                        py={SIDE_PANEL_PADDING_Y}
                    >
                        <LayoutColumn column={layout.columns[0]} context={context} sidePanel />
                    </Box>
                ) : undefined
            }
            center={
                hasLeftColumn ? (
                    <Box height="100%" overflowY="auto">
                        <LayoutColumn column={layout.columns[1]} context={context} pageColumn />
                    </Box>
                ) : (
                    <LayoutColumn column={layout.columns[0]} context={context} pageColumn />
                )
            }
            right={
                rightColumn ? (
                    <Box
                        height="100%"
                        overflowY="auto"
                        px={SIDE_PANEL_PADDING_X}
                        py={SIDE_PANEL_PADDING_Y}
                    >
                        <LayoutColumn column={rightColumn} context={context} sidePanel />
                    </Box>
                ) : undefined
            }
        />
    );
}
